/**
 * Typed call for `GET /statistics/participation` (PLAN-FASE2.md WP-03).
 * Not in the generated `src/api/schema.ts` for the same reason
 * `src/features/statistics/api.ts` and `src/features/vitality/api.ts`
 * aren't: that file is generated from the *deployed* ACC OpenAPI doc, which
 * predates this endpoint entirely. Same hand-built `request()` these two
 * already use rather than a third copy of `client.ts`'s generic machinery.
 */

import { buildUrl, readReason } from '@/api/client';
import { errorForStatus, NetworkError } from '@/api/errors';
import { getAccessToken } from '@/features/auth/token';
import type { DashboardRange, Grain } from '@/features/statistics/api';

async function request<T>(path: string, query: Record<string, string>): Promise<T> {
  // No {placeholders} in this endpoint's path — `query` (buildUrl's third
  // argument) is what turns into the actual query string.
  const url = buildUrl(path, undefined, query);

  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(url, { method: 'GET', headers });
  } catch (cause) {
    throw new NetworkError(`Request to ${path} failed: ${String(cause)}`);
  }

  if (!response.ok) {
    throw errorForStatus(response.status, `GET ${path} failed`, await readReason(response));
  }

  const text = await response.text();
  return JSON.parse(text) as T;
}

/** Sent as `target_type`/`target_id` — see `php/api_participation.php`. */
export type ParticipationTarget = { type: 'company' | 'department'; id: string };

export type FunnelStepKey =
  | 'invited'
  | 'activated'
  | 'active_7d'
  | 'active_30d'
  | 'structurally_active'
  | 'needs_reactivation';

export interface FunnelStep {
  key: FunnelStepKey;
  count: number;
  pct_of_previous: number | null;
}

export interface ParticipationRates {
  daily_sync_pct: number | null;
  ai_coach_use_pct: number | null;
  challenge_participation_pct: number | null;
  notification_open_pct: number | null;
  /** Added alongside `focus_rhythm_pct` for the Energie & werkbeleving
   * card's Pauze-adoptie/Focusritme tiles — see
   * `PLAN-break-focus-adoption-signals.md` in the API repo. Same shape as
   * `ai_coach_use_pct`: share of `activated` users with a matching
   * engagement event (`break_taken`) in range. */
  break_adoption_pct: number | null;
  /** `focus_session_completed` engagement events, same shape as
   * `break_adoption_pct` above. */
  focus_rhythm_pct: number | null;
}

export interface ParticipationBreakdownRow {
  department_id?: string;
  location_id?: string;
  /** Pilot-group rows only — the API has no separate pilots lookup the
   * frontend already fetched (unlike department/location), so this row
   * carries the pilot's own display name directly rather than an id to
   * resolve elsewhere. */
  pilot_group_id?: string;
  name?: string;
  invited: number;
  activated: number;
  active_30d: number;
}

export interface ParticipationBreakdown {
  department: ParticipationBreakdownRow[];
  location: ParticipationBreakdownRow[];
  pilot_group: ParticipationBreakdownRow[];
}

export interface ParticipationFunnel {
  status: 'ok';
  min_group_size: number;
  range: { from: string; to: string };
  funnel: FunnelStep[];
  rates: ParticipationRates;
  breakdown: ParticipationBreakdown;
}

export interface ParticipationInsufficientData {
  status: 'insufficient_data';
  min_group_size: number;
  range?: { from: string; to: string };
}

export type ParticipationResult = ParticipationFunnel | ParticipationInsufficientData;

/** `range` accepts the same values `RangePicker` produces; an explicit
 * `{from,to}` is sent as `YYYY-MM-DD..YYYY-MM-DD` (`php/api_participation.php`'s
 * `range()`), a relative keyword passed through as-is. */
function rangeParam(range: DashboardRange): string {
  return typeof range === 'string' ? range : `${range.from}..${range.to}`;
}

/** One bucket of `GET /statistics/participation/series` (§
 * `PLAN-participation-series.md` in the API repo) — a member counts as
 * active in a bucket if they synced at least once inside it, so at
 * `grain: 'week'` this reads consistently with the funnel's `active_7d`
 * step, just applied once per week instead of once over the whole range.
 * Buckets with zero active members are omitted, same convention
 * `/statistics/dashboard`'s `series` panels use. */
export interface ParticipationSeriesBucket {
  bucket: string;
  first_date: string;
  active_count: number;
}

export interface ParticipationSeriesOk {
  status: 'ok';
  min_group_size: number;
  grain: Grain;
  range: { from: string; to: string };
  series: ParticipationSeriesBucket[];
}

export type ParticipationSeriesResult = ParticipationSeriesOk | ParticipationInsufficientData;

/** `getParticipationSeries`'s own range type: `DashboardRange` plus
 * `'last_91_days'`, the series endpoint's default (13 whole weeks) — kept
 * local rather than added to the shared `RelativeRange` since nothing else
 * (the generic `RangePicker`, `/statistics/dashboard`) offers or accepts
 * that preset. */
export type ParticipationSeriesRange = DashboardRange | 'last_91_days';

export function getParticipation(
  target: ParticipationTarget,
  range: DashboardRange,
  locationId?: string,
): Promise<ParticipationResult> {
  return request('/statistics/participation', {
    target_type: target.type,
    target_id: target.id,
    range: rangeParam(range),
    ...(locationId ? { location_id: locationId } : {}),
  });
}

/** `GET /statistics/participation/series` (§ `PLAN-participation-series.md`
 * in the API repo) — a weekly (or day/month-grain) breakdown of active-user
 * counts, for the "Actieve gebruikers per week" Trends chart. Defaults to
 * `grain: 'week'`/`range: 'last_91_days'`, the endpoint's own defaults, same
 * as `getParticipation` leaves `location_id` to the caller. `role` narrows
 * the pool to managers/employees, same `manager`/`employee` values the
 * dashboard-batch endpoint's `VitalityTarget.role` accepts — passed straight
 * through as a `role` query param. */
export function getParticipationSeries(
  target: ParticipationTarget,
  options: {
    grain?: Grain;
    range?: ParticipationSeriesRange;
    locationId?: string;
    role?: 'manager' | 'employee';
  } = {},
): Promise<ParticipationSeriesResult> {
  const { grain, range, locationId, role } = options;
  return request('/statistics/participation/series', {
    target_type: target.type,
    target_id: target.id,
    ...(grain ? { grain } : {}),
    ...(range ? { range: typeof range === 'string' ? range : rangeParam(range) } : {}),
    ...(locationId ? { location_id: locationId } : {}),
    ...(role ? { role } : {}),
  });
}
